
import React, { useState } from 'react';
import { UploadFile, InvokeLLM } from '../../integrations/Core';
import AiLayout from '../../components/analyze/AiLayout';
import StockUploadArea from '../../components/stock/StockUploadArea';
import StockAnalysisResult from '../../components/stock/StockAnalysisResult';
import { StockAnalysisSchema } from '../../entities/StockAnalysisSchema';
import { Building2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";

export default function StockAnalysisPage() {
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [error, setError] = useState(null);

    const handleFileSelect = async (file) => {
        setImage(file);
        setIsAnalyzing(true);
        setError(null);

        try {
            const { file_url } = await UploadFile({ file });
            setPreview(file_url);

            // Go straight to analysis (validation disabled for now)
            await handleAnalyze(file_url);
        } catch (err) {
            console.error("Upload failed", err);
            setError("Gagal upload gambar. Sila cuba lagi.");
            setIsAnalyzing(false);
        }
    };

    const validateChartImage = async (fileUrl) => {
        try {
            const result = await InvokeLLM({
                prompt: `Look at this image. Does it contain ANY of the following:
- Candlestick chart (red/green candles)
- Line chart showing price movement
- Bar chart showing price data
- TradingView interface or screenshot
- MT4/MT5 trading platform
- Any trading platform interface with price chart
- Stock/Forex/Crypto price chart
- Bursa Malaysia stock chart
- FCPO chart
- Technical indicators like moving averages, RSI, MACD

Answer with JSON:
{"is_chart": true} - if the image shows ANY price chart or trading platform screenshot
{"is_chart": false} - ONLY if the image is clearly NOT related to trading (like selfie, meme, food photo, etc.)

Be lenient. If there's ANY doubt, return true.`,
                file_urls: [fileUrl],
                response_json_schema: {
                    type: "object",
                    properties: {
                        is_chart: { type: "boolean" }
                    },
                    required: ["is_chart"]
                }
            });

            return result?.is_chart === true;
        } catch (err) {
            console.error("Chart validation failed", err);
            return true; // Fail open
        }
    };

    const handleAnalyze = async (fileUrl) => {
        if (!fileUrl) return;

        setIsAnalyzing(true);
        setError(null);

        try {
            const prompt = `Analyze this Malaysian Stock chart (Bursa Malaysia). Identify the stock code if visible. Focus on Technical Analysis (Trend, Support/Resistance) and Sentiment. Provide a comprehensive investment plan including Target Price and Stop Loss. adhere strictly to the response schema.`;

            const result = await InvokeLLM({
                prompt: prompt,
                file_urls: [fileUrl],
                response_json_schema: StockAnalysisSchema
            });

            // Enriched result
            const enrichedResult = {
                ...result,
                image_url: fileUrl,
                analysis_type: 'stock',
                created_date: new Date().toISOString()
            };

            setAnalysisResult(enrichedResult);

        } catch (err) {
            console.error("Analysis failed", err);
            setError("Gagal menganalisis chart saham. Pastikan gambar jelas.");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleReset = () => {
        setImage(null);
        setPreview(null);
        setAnalysisResult(null);
        setError(null);
        setIsAnalyzing(false);
    };

    return (
        <AiLayout>
            <div className="max-w-5xl mx-auto">
                <div className="mb-8 pl-4 border-l-4 border-red-500">
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-red-500" />
                        AI Stock Analyzer (Malaysia 🇲🇾)
                    </h1>
                    <p className="text-slate-400">
                        Analisis teknikal & fundamental saham Bursa Malaysia dengan kecerdasan buatan.
                    </p>
                </div>

                {error && (
                    <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-800 text-white">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {isAnalyzing ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px] animate-pulse">
                        <div className="w-20 h-20 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <h3 className="text-xl font-semibold text-white">Menganalisis Chart...</h3>
                        <p className="text-slate-400 mt-2">Sedang membaca pattern & mengambil data fundamental...</p>
                    </div>
                ) : !analysisResult ? (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <StockUploadArea
                            onImageUpload={handleFileSelect}
                        />
                    </div>
                ) : (
                    <StockAnalysisResult
                        analysis={analysisResult}
                        onNewAnalysis={handleReset}
                    />
                )}
            </div>
        </AiLayout>
    );
}
